import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GA_EVENTS, trackEvent } from '../../utils/analytics';
import { easeOutCubic, shouldAnimate } from '../../utils/motion';

// Same formula as /herramientas/ahorro-automatizacion: people × hours/day × cost × 22 working days.
const WORKING_DAYS = 22;

const FIELDS = [
  { id: 'people', label: 'Personas que hacen la tarea', min: 1, max: 20, step: 1, format: (v) => `${v}` },
  { id: 'hours', label: 'Horas manuales al día (por persona)', min: 0.5, max: 6, step: 0.5, format: (v) => `${v} h` },
  { id: 'cost', label: 'Costo por hora (MXN)', min: 60, max: 600, step: 10, format: (v) => `$${v}` },
];

const formatMoney = (value) => `$${Math.round(value).toLocaleString('es-MX')}`;

export default function SavingsCalculator({ toolHref, ctaHref }) {
  const [values, setValues] = useState({ people: 2, hours: 1.5, cost: 150 });
  const [shown, setShown] = useState(null);
  const [pulse, setPulse] = useState(false);
  const shownRef = useRef(null);
  const started = useRef(false);

  const monthly = values.people * values.hours * values.cost * WORKING_DAYS;

  useEffect(() => {
    if (shownRef.current === null || !shouldAnimate()) {
      shownRef.current = monthly;
      setShown(monthly);
      return undefined;
    }

    const from = shownRef.current;
    const start = performance.now();
    let frame = 0;
    setPulse(true);

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / 350);
      const next = from + (monthly - from) * easeOutCubic(progress);
      shownRef.current = next;
      setShown(next);
      if (progress < 1) frame = window.requestAnimationFrame(tick);
      else setPulse(false);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [monthly]);

  const handleChange = (id, value) => {
    if (!started.current) {
      started.current = true;
      trackEvent(GA_EVENTS.INICIAR_CALCULADORA, { tool: 'ahorro-automatizacion', source: 'home' });
    }
    setValues((current) => ({ ...current, [id]: Number(value) }));
  };

  return (
    <form className="home-calc" onSubmit={(event) => event.preventDefault()} aria-labelledby="home-calc-title">
      <div>
        <span className="eyebrow-text">Negocio · Destacada</span>
        <h3 id="home-calc-title">Calculadora de ahorro por automatización</h3>
      </div>
      <div className="home-calc-fields">
        {FIELDS.map((field) => {
          const value = values[field.id];
          const fill = ((value - field.min) / (field.max - field.min)) * 100;
          return (
            <label key={field.id} htmlFor={`home-calc-${field.id}`}>
              <span>{field.label}</span>
              <output htmlFor={`home-calc-${field.id}`}>{field.format(value)}</output>
              <input
                id={`home-calc-${field.id}`}
                type="range"
                min={field.min}
                max={field.max}
                step={field.step}
                value={value}
                style={{ '--fill': `${fill}%` }}
                onChange={(event) => handleChange(field.id, event.target.value)}
              />
            </label>
          );
        })}
      </div>
      <div className="home-calc-result">
        <div>
          <small>Costo mensual que podrías recuperar</small>
          <strong className={pulse ? 'is-updating' : ''} aria-live="polite">
            {formatMoney(shown ?? monthly)} / mes
          </strong>
          <small>{formatMoney(monthly * 12)} al año · estimación</small>
        </div>
        <div className="home-calc-actions">
          <Link
            to={ctaHref}
            className="btn-cta"
            data-magnetic
            onClick={() => trackEvent(GA_EVENTS.CLICK_CTA_SERVICIO, { source: 'home_calculator', monthly: Math.round(monthly) })}
          >
            Quiero automatizarlo
          </Link>
          <Link to={toolHref} className="text-link">Ver calculadora completa</Link>
        </div>
      </div>
    </form>
  );
}
